import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { leadsAPI, channelsAPI, microsoftAPI, settingsAPI } from '../services/api';
import { timeAgo, formatDuration } from '../utils/dateUtils';
import QuickAddLead from '../components/common/QuickAddLead';
import Skeleton from '../components/common/Skeleton';
import EmptyState from '../components/common/EmptyState';
import ErrorState from '../components/common/ErrorState';
import {
    PieChart, Pie, Cell, Legend, Tooltip, ResponsiveContainer,
    LineChart, Line, XAxis, YAxis, CartesianGrid, ReferenceLine
} from 'recharts';
import ImportLeadsModal from '../components/common/ImportLeadsModal';
import OnboardingModal from '../components/OnboardingModal';
import { useAuth } from '../context/AuthContext';

const Dashboard = () => {
    const { isDark } = useTheme();
    const { user } = useAuth();
    const navigate = useNavigate();

    // State
    const [stats, setStats] = useState(null);
    const [recentLeads, setRecentLeads] = useState([]);
    const [leadsOverTimeData, setLeadsOverTimeData] = useState([]);
    const [avgRespOverTimeData, setAvgRespOverTimeData] = useState([]);
    const [leadsBySourceData, setLeadsBySourceData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [error, setError] = useState(null);
    const [timeRange, setTimeRange] = useState('week');
    const [showQuickAdd, setShowQuickAdd] = useState(false);
    const [showImport, setShowImport] = useState(false);
    const [hasConnectedChannel, setHasConnectedChannel] = useState(true); // Default true to avoid flash
    const [checkingChannels, setCheckingChannels] = useState(true);
    const [hasEnabledSources, setHasEnabledSources] = useState(true);
    const [checkingSources, setCheckingSources] = useState(true);
    const [showOnboarding, setShowOnboarding] = useState(false);
    const [tokenExpired, setTokenExpired] = useState(false);
    const [expiredChannelName, setExpiredChannelName] = useState('');

    useEffect(() => {
        if (user && user.onboarded === false) {
            setShowOnboarding(true);
        }
    }, [user]);

    // Fetch data
    const fetchDashboardData = async () => {
        try {
            if (!stats) setLoading(true);
            else setIsRefreshing(true);
            setError(null);

            // Fetch stats
            const statsRes = await leadsAPI.getStats({ range: timeRange });
            const dashboardStats = statsRes.data?.stats || statsRes.data;
            setStats(dashboardStats);

            // Fetch recent leads
            const leadsRes = await leadsAPI.getLeads({
                limit: 5,
                sortBy: 'created_at',
                sortOrder: 'desc'
            });
            const leads = leadsRes.data?.leads || leadsRes.data || [];
            setRecentLeads(leads);

            // Fetch channel status
            if (!stats) setCheckingChannels(true);
            const [gmailRes, msRes, imapRes] = await Promise.allSettled([
                channelsAPI.getGmailStatus(),
                microsoftAPI.getStatus(),
                channelsAPI.getIMAPStatus()
            ]);

            const isGmailConnected = gmailRes.status === 'fulfilled' && gmailRes.value.data?.connected;
            const isMsConnected = msRes.status === 'fulfilled' && msRes.value.data?.connected;
            const isImapConnected = imapRes.status === 'fulfilled' && imapRes.value.data?.connected;

            // Check for expired tokens (OAuth only)
            const gmailExpired = gmailRes.status === 'fulfilled' && gmailRes.value.data?.connected && gmailRes.value.data?.token_expired;
            const msExpired = msRes.status === 'fulfilled' && msRes.value.data?.connected && msRes.value.data?.token_expired;
            setTokenExpired(gmailExpired || msExpired);
            setExpiredChannelName(gmailExpired && msExpired ? 'Gmail and Outlook' : gmailExpired ? 'Gmail' : msExpired ? 'Outlook' : '');

            setHasConnectedChannel(isGmailConnected || isMsConnected || isImapConnected);
            setCheckingChannels(false);

            // Fetch lead sources
            if (!stats) setCheckingSources(true);
            try {
                const sourcesRes = await settingsAPI.getLeadSources();
                const sources = sourcesRes.data?.sources || [];
                const anyEnabled = sources.some(s => s.is_enabled);
                setHasEnabledSources(anyEnabled);
            } catch (err) {
                console.error("Failed to fetch lead sources", err);
                setHasEnabledSources(true);
            }
            setCheckingSources(false);

            if (!stats) setLoading(false);
            setIsRefreshing(false);
        } catch (error) {
            console.error('Error fetching dashboard data:', error);
            setError(error.message || 'Failed to load dashboard data. Please check your connection.');
            setLoading(false);
            setIsRefreshing(false);
        }
    };

    // Fetch data effect
    useEffect(() => {
        fetchDashboardData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [timeRange]);

    // Helper functions

    const getSourceColor = (source) => {
        // Theme-based palette
        const themePalette = isDark ? [
            '#08D9D6', // cyan
            '#FF2E63', // pink
            '#00ADB5', // teal-bright
            '#F0A500', // amber-gold
            '#B829F2', // purple-neon
            '#4ECCA3'  // green-neon
        ] : [
            '#1DCBB3', // primary
            '#0E8A8F', // teal
            '#FF2E63', // pink
            '#FF9F1C', // orange-vibrant
            '#2B3A38', // darkTeal
            '#5D5FEF'  // indigo
        ];

        const sourceMap = {
            'zillow': 0,
            'realtor': 1,
            'website': 2,
            'web': 2,
            'facebook': 5,
            'instagram': 4,
            'whatsapp': 3,
            'rightmove': 3,
            '99acres': 5,
            'redfin': 1
        };

        const index = sourceMap[source?.toLowerCase()] ??
            (source?.length ? source.charCodeAt(0) % themePalette.length : 0);

        return themePalette[index % themePalette.length];
    };

    const getStatusColor = (status) => {
        switch (status?.toUpperCase()) {
            case 'NEW': return 'bg-cyan/10 text-cyan border border-cyan/30';
            case 'CONTACTED': return 'bg-amber-500/10 text-amber-500 border border-amber-500/30';
            case 'QUALIFIED': return 'bg-blue-500/10 text-blue-500 border border-blue-500/30';
            case 'CLOSED': return 'bg-purple-500/10 text-purple-500 border border-purple-500/30';
            case 'ARCHIVED': return 'bg-gray-700/30 text-gray-400 border border-gray-600';
            default: return 'bg-gray-500/10 text-gray-500';
        }
    };

    const getSourceIconParams = (source) => {
        switch (source?.toLowerCase()) {
            case 'zillow': return { icon: 'home', colorClass: 'text-blue-500' };
            case 'realtor': return { icon: 'real_estate_agent', colorClass: 'text-blue-500' };
            case 'whatsapp': return { icon: 'chat', colorClass: 'text-emerald-500' };
            case 'web':
            case 'website': return { icon: 'language', colorClass: 'text-purple-500' };
            case 'facebook': return { icon: 'public', colorClass: 'text-blue-600' }; // facebook blue
            default: return { icon: 'public', colorClass: 'text-light/60' };
        }
    };

    // Derived state effect - updates when stats or theme changes
    useEffect(() => {
        if (!stats) return;

        // Process Leads Over Time and Response Time Over Time
        if (stats.leadsLast7Days && stats.dayLabels) {
            const overTimeData = stats.leadsLast7Days.map((count, index) => ({
                day: stats.dayLabels[index],
                leads: count
            }));
            setLeadsOverTimeData(overTimeData);

            if (stats.avgRespOverTime) {
                const respOverTimeData = stats.avgRespOverTime.map((avgSecs, index) => ({
                    day: stats.dayLabels[index],
                    avgResponse: avgSecs
                }));
                setAvgRespOverTimeData(respOverTimeData);
            }
        }

        // Process Leads By Source with Alternating Shades
        if (stats.leadsBySource) {
            const palette = isDark ? [
                '#08D9D6', // Cyan Neon
                '#115E59', // Cyan Dark
                '#FF2E63', // Pink Neon
                '#881337', // Pink Dark
                '#A855F7', // Purple Neon
                '#581C87', // Purple Dark
                '#F0A500', // Amber Neon
                '#78350F'  // Amber Dark
            ] : [
                '#0E8A8F', // Teal Strong
                '#5EEAD4', // Teal Light
                '#BE185D', // Pink Strong
                '#F472B6', // Pink Light
                '#7E22CE', // Purple Strong
                '#C084FC', // Purple Light
                '#B45309', // Amber Strong
                '#FBBF24'  // Amber Light
            ];

            const sourceData = stats.leadsBySource.map((source, index) => ({
                name: source.name,
                value: source.count,
                color: palette[index % palette.length]
            }));
            setLeadsBySourceData(sourceData);
        }
    }, [stats, isDark]);

    if (loading) {
        return (
            <div className="bg-mint dark:bg-navy min-h-screen p-6">
                <div className="flex justify-between items-end mb-8">
                    <div className="w-1/3">
                        <Skeleton.Text lines={2} />
                    </div>
                </div>

                {/* Stats Grid Skeleton */}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                    {[1, 2, 3, 4].map(i => (
                        <div key={i} className="bg-card rounded-xl p-5 border border-white/5 h-32 animate-pulse">
                            <div className="h-4 w-1/2 bg-slate-800 rounded mb-4"></div>
                            <div className="h-8 w-1/3 bg-slate-700 rounded mb-2"></div>
                            <div className="h-3 w-1/4 bg-slate-800 rounded"></div>
                        </div>
                    ))}
                </div>

                {/* Hero Card Skeleton */}
                <div className="mb-8 bg-card rounded-xl border border-white/5 h-96 animate-pulse p-8">
                    <div className="h-6 w-1/4 bg-slate-800 rounded mb-8"></div>
                    <div className="space-y-4">
                        <div className="h-4 w-full bg-slate-800 rounded"></div>
                        <div className="h-4 w-5/6 bg-slate-800 rounded"></div>
                        <div className="h-4 w-4/6 bg-slate-800 rounded"></div>
                    </div>
                </div>

                {/* Table Skeleton */}
                <div className="bg-card rounded-xl border border-white/5 p-6 h-64 overflow-hidden">
                    <Skeleton.Table rows={3} />
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="p-6 md:p-12 h-[calc(100vh-4rem)] flex items-center justify-center">
                <ErrorState message={error} onRetry={fetchDashboardData} className="w-full max-w-lg" />
            </div>
        );
    }

    return (
        <div className="bg-mint dark:bg-navy min-h-screen">
            {showOnboarding && <OnboardingModal onComplete={() => setShowOnboarding(false)} />}

            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-3 mb-6 md:mb-8">
                <div>
                    <h2 className="text-2xl md:text-3xl font-bold text-darkTeal dark:text-light mb-1 text-left">Dashboard</h2>
                    <p className="text-darkTeal/60 dark:text-light/60 text-sm text-left">Overview of your lead activity and performance.</p>
                </div>
                <div className="flex flex-wrap gap-2 md:gap-3 items-end">
                    <button
                        onClick={fetchDashboardData}
                        disabled={isRefreshing}
                        className="px-3 py-1.5 md:px-4 md:py-2 bg-white dark:bg-navy border border-teal/30 dark:border-cyan/30 text-teal dark:text-cyan hover:bg-teal/5 dark:hover:bg-cyan/10 font-medium rounded-lg transition-colors flex items-center gap-2 text-xs md:text-sm disabled:opacity-50"
                    >
                        <span className={`material-symbols-outlined text-base ${isRefreshing ? 'animate-spin' : ''}`}>refresh</span>
                        Refresh
                    </button>
                    <button
                        onClick={() => setShowImport(true)}
                        className="px-3 py-1.5 md:px-4 md:py-2 bg-white dark:bg-navy border border-teal/30 dark:border-cyan/30 text-teal dark:text-cyan hover:bg-teal/5 dark:hover:bg-cyan/10 font-medium rounded-lg transition-colors flex items-center gap-2 text-xs md:text-sm"
                    >
                        <span className="material-symbols-outlined text-base">upload_file</span>
                        Import
                    </button>
                    <button
                        onClick={() => setShowQuickAdd(true)}
                        className="px-3 py-1.5 md:px-4 md:py-2 bg-primary dark:bg-cyan hover:bg-primary/80 dark:hover:bg-cyan/80 text-white dark:text-navy font-bold rounded-lg shadow-[0_0_15px_rgba(37,176,155,0.3)] dark:shadow-[0_0_15px_rgba(8,217,214,0.3)] hover:shadow-[0_0_20px_rgba(37,176,155,0.5)] dark:hover:shadow-[0_0_20px_rgba(8,217,214,0.5)] transition-all flex items-center gap-2 text-xs md:text-sm"
                    >
                        <span className="material-symbols-outlined text-base">add</span>
                        Quick Add Lead
                    </button>
                </div>
            </div>

            {/* Connection Warning Banner */}
            {!checkingChannels && !hasConnectedChannel && (
                <div className="mb-6 bg-red-500/10 border border-red-500/20 rounded-xl p-4 flex flex-col md:flex-row items-center justify-between gap-4 animate-fade-in">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-red-500/20 flex items-center justify-center text-red-500 shrink-0">
                            <span className="material-symbols-outlined">link_off</span>
                        </div>
                        <div>
                            <h3 className="font-bold text-red-500 text-sm md:text-base text-left">No Email Channel Connected</h3>
                            <p className="text-xs md:text-sm text-red-500/80">Connect Gmail, Outlook, or IMAP to start receiving leads automatically.</p>
                        </div>
                    </div>
                    <Link
                        to="/channels"
                        className="w-full md:w-auto px-4 py-2 bg-red-500 text-white font-bold rounded-lg text-sm hover:bg-red-600 transition-colors flex items-center justify-center gap-2 shadow-lg shadow-red-500/20"
                    >
                        <span className="material-symbols-outlined text-lg">add_link</span>
                        Connect Channel
                    </Link>
                </div>
            )}

            {/* Token Expired Warning Banner */}
            {tokenExpired && (
                <div className="mb-6 bg-amber-500/10 border border-amber-500/20 rounded-xl p-4 flex flex-col md:flex-row items-center justify-between gap-4 animate-fade-in">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-amber-500/20 flex items-center justify-center text-amber-500 shrink-0">
                            <span className="material-symbols-outlined">warning</span>
                        </div>
                        <div>
                            <h3 className="font-bold text-amber-600 dark:text-amber-400 text-sm md:text-base text-left">Email Monitoring Paused</h3>
                            <p className="text-xs md:text-sm text-amber-600/80 dark:text-amber-400/80">Your {expiredChannelName} connection expired. Reconnect now to resume automatic lead capture and responses.</p>
                        </div>
                    </div>
                    <Link
                        to="/channels"
                        className="w-full md:w-auto px-4 py-2 bg-amber-500 text-white font-bold rounded-lg text-sm hover:bg-amber-600 transition-colors flex items-center justify-center gap-2 shadow-lg shadow-amber-500/20 whitespace-nowrap"
                    >
                        <span className="material-symbols-outlined text-lg">link</span>
                        Go to Channels
                    </Link>
                </div>
            )}

            {/* No Sources Warning Banner */}
            {!checkingSources && !hasEnabledSources && (
                <div className="mb-6 bg-amber-500/10 border border-amber-500/20 rounded-xl p-4 flex flex-col md:flex-row items-center justify-between gap-4 animate-fade-in">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-amber-500/20 flex items-center justify-center text-amber-500 shrink-0">
                            <span className="material-symbols-outlined">filter_list_off</span>
                        </div>
                        <div>
                            <h3 className="font-bold text-amber-500 text-sm md:text-base text-left">No Lead Sources Enabled</h3>
                            <p className="text-xs md:text-sm text-amber-500/80">Select platforms (Zillow, Realtor, etc.) to start capturing leads.</p>
                        </div>
                    </div>
                    <Link
                        to="/lead-sources"
                        state={{ section: 'lead-sources' }}
                        className="w-full md:w-auto px-4 py-2 bg-amber-500 text-white font-bold rounded-lg text-sm hover:bg-amber-600 transition-colors flex items-center justify-center gap-2 shadow-lg shadow-amber-500/20"
                    >
                        <span className="material-symbols-outlined text-lg">settings</span>
                        Configure Sources
                    </Link>
                </div>
            )}

            {/* Top Stats Cards - ROW 1: Your Performance */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4 mb-6 md:mb-8">

                {/* Leads Today */}
                <div className="bg-mint dark:bg-navy rounded-xl p-3 md:p-5 border border-primary/50 dark:border-cyan/50 shadow-[0_4px_20px_rgba(48,227,202,0.1)] dark:shadow-[0_4px_20px_rgba(8,217,214,0.1)] relative overflow-hidden group">
                    <div className="relative z-10">
                        <div className="flex justify-between items-start mb-3">
                            <p className="text-teal dark:text-cyan font-medium text-sm">Leads Today</p>
                            <span className="bg-primary/10 dark:bg-cyan/10 p-1.5 rounded-lg text-teal dark:text-cyan">
                                <span className="material-symbols-outlined text-xs">today</span>
                            </span>
                        </div>
                        <h3 className="text-2xl md:text-3xl font-bold text-darkTeal dark:text-light mb-1 text-left">{stats?.todayCount || 0}</h3>
                        <div className="flex items-center gap-2 text-xs">
                            <span className={`${(stats?.leadsGrowth || 0) >= 0 ? 'bg-primary/20 dark:bg-cyan/20 text-teal dark:text-cyan' : 'bg-red-500/10 text-red-500'} px-1.5 py-0.5 rounded font-bold`}>
                                {(stats?.leadsGrowth || 0) > 0 ? '+' : ''}{stats?.leadsGrowth || 0}%
                            </span>
                            <span className="text-darkTeal/50 dark:text-light/50">vs yesterday</span>
                        </div>
                    </div>
                    <div className="absolute -right-6 -bottom-6 w-24 h-24 bg-primary/10 dark:bg-cyan/10 rounded-full blur-xl group-hover:bg-primary/20 dark:group-hover:bg-cyan/20 transition-all duration-500"></div>
                </div>

                {/* This Month */}
                <div className="bg-mint dark:bg-navy rounded-xl p-3 md:p-5 border border-primary/50 dark:border-cyan/50 shadow-[0_4px_20px_rgba(48,227,202,0.1)] dark:shadow-[0_4px_20px_rgba(8,217,214,0.1)] relative overflow-hidden group">
                    <div className="relative z-10">
                        <div className="flex justify-between items-start mb-3">
                            <p className="text-teal dark:text-cyan font-medium text-sm">This Month</p>
                            <span className="bg-primary/10 dark:bg-cyan/10 p-1.5 rounded-lg text-teal dark:text-cyan">
                                <span className="material-symbols-outlined text-xs">calendar_month</span>
                            </span>
                        </div>
                        <h3 className="text-2xl md:text-3xl font-bold text-darkTeal dark:text-light mb-1 text-left">{stats?.leadsThisMonth || 0}</h3>
                        <div className="flex items-center gap-2 text-xs">
                            <span className={`${(stats?.monthlyGrowth || 0) >= 0 ? 'bg-primary/20 dark:bg-cyan/20 text-teal dark:text-cyan' : 'bg-red-500/10 text-red-500'} px-1.5 py-0.5 rounded font-bold`}>
                                {(stats?.monthlyGrowth || 0) > 0 ? '+' : ''}{stats?.monthlyGrowth || 0}%
                            </span>
                            <span className="text-darkTeal/50 dark:text-light/50">vs last month</span>
                        </div>
                    </div>
                    <div className="absolute -right-6 -bottom-6 w-24 h-24 bg-primary/10 dark:bg-cyan/10 rounded-full blur-xl group-hover:bg-primary/20 dark:group-hover:bg-cyan/20 transition-all duration-500"></div>
                </div>

                {/* Total Leads */}
                <div className="bg-mint dark:bg-navy rounded-xl p-3 md:p-5 border border-primary/50 dark:border-cyan/50 shadow-[0_4px_20px_rgba(48,227,202,0.1)] dark:shadow-[0_4px_20px_rgba(8,217,214,0.1)] relative overflow-hidden group">
                    <div className="relative z-10">
                        <div className="flex justify-between items-start mb-3">
                            <p className="text-teal dark:text-cyan font-medium text-sm">Total Leads</p>
                            <span className="bg-primary/10 dark:bg-cyan/10 p-1.5 rounded-lg text-teal dark:text-cyan">
                                <span className="material-symbols-outlined text-xs">group</span>
                            </span>
                        </div>
                        <h3 className="text-2xl md:text-3xl font-bold text-darkTeal dark:text-light mb-1 text-left">{stats?.total || 0}</h3>
                        <div className="flex items-center gap-2 text-xs">
                            <span className="text-darkTeal/50 dark:text-light/50">All time</span>
                        </div>
                    </div>
                    <div className="absolute -right-6 -bottom-6 w-24 h-24 bg-primary/10 dark:bg-cyan/10 rounded-full blur-xl group-hover:bg-primary/20 dark:group-hover:bg-cyan/20 transition-all duration-500"></div>
                </div>

                {/* Conversion Rate */}
                <div className="bg-mint dark:bg-navy rounded-xl p-3 md:p-5 border border-teal/20 dark:border-light/20 hover:border-primary/50 dark:hover:border-cyan/50 shadow-sm transition-colors group text-left">
                    <div className="flex justify-between items-start mb-3">
                        <p className="text-darkTeal/70 dark:text-light/70 group-hover:text-darkTeal dark:group-hover:text-light transition-colors font-medium text-sm">Conversion Rate</p>
                        <span className="bg-teal/5 dark:bg-light/5 text-darkTeal/50 dark:text-light/50 p-1.5 rounded-lg group-hover:text-primary dark:group-hover:text-cyan group-hover:bg-primary/10 dark:group-hover:bg-cyan/10 transition-colors">
                            <span className="material-symbols-outlined text-xs">pie_chart</span>
                        </span>
                    </div>
                    <h3 className="text-2xl md:text-3xl font-bold text-darkTeal dark:text-light mb-1 text-left">{stats?.conversionRate || 0}<span className="text-base text-darkTeal/40 dark:text-light/40 font-normal ml-1">%</span></h3>
                    <div className="flex items-center gap-2 text-xs">
                        <span className={`${(stats?.conversionGrowth || 0) >= 0 ? 'bg-primary/20 dark:bg-cyan/20 text-teal dark:text-cyan' : 'bg-red-500/10 text-red-500'} px-1.5 py-0.5 rounded font-bold`}>
                            {(stats?.conversionGrowth || 0) > 0 ? '+' : ''}{stats?.conversionGrowth || 0}%
                        </span>
                        <span className="text-darkTeal/50 dark:text-light/50">vs last week</span>
                    </div>
                </div>
            </div>

            {/* Response Time Analytics Hero Card */}
            {stats && (
                <div className="mb-6 md:mb-8 bg-mint dark:bg-navy rounded-xl border border-teal/20 dark:border-light/20 shadow-lg overflow-hidden flex flex-col lg:flex-row">
                    {/* Stats & Comparison Column */}
                    <div className="w-full lg:w-1/3 p-6 md:p-8 flex flex-col justify-between border-b lg:border-b-0 lg:border-r border-teal/10 dark:border-light/10 relative overflow-hidden group">
                        <div className="relative z-10">
                            <div className="flex items-center gap-2 mb-6">
                                <span className="material-symbols-outlined text-primary dark:text-cyan">avg_time</span>
                                <h3 className="font-bold text-darkTeal dark:text-light uppercase tracking-widest text-sm text-left">Response Time Analytics</h3>
                            </div>

                            <div className="space-y-6">
                                {/* Your Performance */}
                                <div>
                                    <p className="text-[10px] font-black uppercase tracking-[0.2em] text-darkTeal/40 dark:text-light/40 mb-3 text-left">Your Performance</p>
                                    <div className="space-y-4">
                                        <div className="flex justify-between items-start">
                                            <div className="flex flex-col text-left">
                                                <span className="text-sm font-medium text-darkTeal/70 dark:text-light/70 mb-1">Average response</span>
                                                <div className="flex items-center gap-2 text-xs">
                                                    <span className={`${(stats?.responseStatsDiff || 0) <= 0 ? 'text-teal dark:text-green-400 bg-teal/10 dark:bg-green-900/30' : 'text-red-500 bg-red-500/10'} px-1.5 py-0.5 rounded font-bold flex items-center`}>
                                                        <span className="material-symbols-outlined text-[10px] mr-0.5">{(stats?.responseStatsDiff || 0) <= 0 ? 'arrow_downward' : 'arrow_upward'}</span>
                                                        {Math.abs(stats?.responseStatsDiff || 0)}%
                                                    </span>
                                                    <span className="text-darkTeal/40 dark:text-light/40">vs 7d avg</span>
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <h3 className="text-2xl font-black text-darkTeal dark:text-light leading-none">{formatDuration(stats.avgResponseTime)}</h3>
                                                <span className="bg-teal/5 dark:bg-light/5 text-darkTeal/50 dark:text-light/50 p-1.5 rounded-lg group-hover:text-primary dark:group-hover:text-cyan group-hover:bg-primary/10 dark:group-hover:bg-cyan/10 transition-colors hidden md:block">
                                                    <span className="material-symbols-outlined text-sm">timer</span>
                                                </span>
                                            </div>
                                        </div>

                                        <div className="border-b border-teal/5 dark:border-light/5"></div>

                                        <div className="flex justify-between items-end">
                                            <span className="text-sm font-medium text-darkTeal/70 dark:text-light/70">Fastest response</span>
                                            <span className="text-lg font-bold text-primary dark:text-cyan">{stats.fastestResponseTime != null ? formatDuration(stats.fastestResponseTime) : '-'}</span>
                                        </div>
                                        <div className="flex justify-between items-end">
                                            <span className="text-sm font-medium text-darkTeal/70 dark:text-light/70">This month</span>
                                            <span className="text-base font-bold text-darkTeal/90 dark:text-light/90">{stats.respondedThisMonth || 0} leads responded to</span>
                                        </div>
                                    </div>
                                </div>

                                {/* Comparison */}
                                <div className="pt-6 border-t border-teal/10 dark:border-light/10">
                                    <p className="text-[10px] font-black uppercase tracking-[0.2em] text-darkTeal/40 dark:text-light/40 mb-3 text-left">Industry Comparison</p>
                                    <div className="flex items-center justify-between">
                                        <div className="text-left">
                                            <p className="text-xs text-darkTeal/50 dark:text-light/50 font-medium mb-1">Industry Average</p>
                                            <p className="text-sm font-bold text-darkTeal/70 dark:text-light/70">47 minutes</p>
                                        </div>
                                        <div className="text-right">
                                            {(() => {
                                                const industryAvgSecs = 2820; // 47 mins
                                                if (stats.avgResponseTime && stats.avgResponseTime < industryAvgSecs) {
                                                    const fasterPercent = Math.round(((industryAvgSecs - stats.avgResponseTime) / industryAvgSecs) * 100);
                                                    return (
                                                        <>
                                                            <p className="text-xs font-bold text-green-500 uppercase tracking-wider mb-1">You are</p>
                                                            <p className="text-3xl font-black text-green-500 leading-none">{fasterPercent}% faster</p>
                                                        </>
                                                    );
                                                } else if (stats.avgResponseTime) {
                                                    return (
                                                        <>
                                                            <p className="text-xs font-bold text-amber-500 uppercase tracking-wider mb-1">You are</p>
                                                            <p className="text-xl font-black text-amber-500 leading-none">Slower</p>
                                                        </>
                                                    );
                                                }
                                                return <p className="text-sm text-darkTeal/40 dark:text-light/40">Not enough data</p>;
                                            })()}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        {/* Decorative background element showing relative position */}
                        <div className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-1/2 w-48 h-48 bg-primary/5 dark:bg-cyan/5 rounded-full blur-2xl pointer-events-none group-hover:bg-primary/10 dark:group-hover:cyan/10 transition-colors duration-700"></div>
                    </div>

                    {/* Chart Column */}
                    <div className="w-full lg:w-2/3 p-6 md:p-8 bg-mint dark:bg-navy relative z-0">
                        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4">
                            <div>
                                <h4 className="text-sm font-bold text-darkTeal dark:text-light text-left">Response Time Trend</h4>
                                <p className="text-[11px] text-darkTeal/50 dark:text-light/50 mt-1">Lower is better. Track your speed over the last 7 days.</p>
                            </div>
                        </div>

                        <div className="h-[240px] w-full relative">
                            {avgRespOverTimeData.length > 0 && avgRespOverTimeData.some(d => d.avgResponse > 0) ? (
                                <ResponsiveContainer width="100%" height="100%">
                                    <LineChart data={avgRespOverTimeData} margin={{ top: 20, right: 30, left: 0, bottom: 0 }}>
                                        <defs>
                                            <linearGradient id="colorAvgResp" x1="0" y1="0" x2="0" y2="1">
                                                <stop offset="5%" stopColor={isDark ? "#14b8a6" : "#25b09b"} stopOpacity={0.3} />
                                                <stop offset="95%" stopColor={isDark ? "#14b8a6" : "#25b09b"} stopOpacity={0} />
                                            </linearGradient>
                                            <filter id="shadowResp" height="200%">
                                                <feDropShadow dx="0" dy="4" stdDeviation="5" floodColor={isDark ? "#08D9D6" : "#25b09b"} floodOpacity="0.3" />
                                            </filter>
                                        </defs>
                                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)'} />
                                        <XAxis
                                            dataKey="day"
                                            axisLine={false}
                                            tickLine={false}
                                            tick={{ fill: isDark ? 'rgba(255,255,255,0.4)' : 'rgba(43,58,56,0.5)', fontSize: 11, fontWeight: 500 }}
                                            dy={10}
                                        />
                                        <YAxis
                                            axisLine={false}
                                            tickLine={false}
                                            tick={{ fill: isDark ? 'rgba(255,255,255,0.4)' : 'rgba(43,58,56,0.5)', fontSize: 11, fontWeight: 500 }}
                                            width={40}
                                            tickFormatter={(value) => `${value}s`}
                                            domain={[0, 'dataMax + 10']}
                                        />
                                        <Tooltip
                                            contentStyle={{
                                                backgroundColor: isDark ? '#0f172a' : '#ffffff',
                                                borderColor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(37,176,155,0.2)',
                                                borderRadius: '12px',
                                                boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)',
                                                padding: '12px',
                                                fontWeight: 'bold',
                                                color: isDark ? '#f8fafc' : '#1e293b'
                                            }}
                                            itemStyle={{ fontWeight: '500' }}
                                            labelStyle={{ color: isDark ? 'rgba(255,255,255,0.5)' : 'rgba(43,58,56,0.6)', marginBottom: '4px', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.05em' }}
                                            formatter={(value) => [`${value} seconds`, 'Avg Speed']}
                                        />
                                        <ReferenceLine
                                            y={60}
                                            stroke="rgba(239, 68, 68, 0.5)"
                                            strokeDasharray="3 3"
                                            label={{ position: 'top', value: '1 Min Goal', fill: 'rgba(239, 68, 68, 0.7)', fontSize: 10, fontWeight: 'bold' }}
                                        />
                                        <Line
                                            type="monotone"
                                            dataKey="avgResponse"
                                            stroke={isDark ? "#14b8a6" : "#25b09b"}
                                            strokeWidth={3}
                                            dot={{ r: 4, strokeWidth: 2, fill: isDark ? '#0f172a' : '#ffffff', stroke: isDark ? "#14b8a6" : "#25b09b" }}
                                            activeDot={{ r: 6, strokeWidth: 0, fill: isDark ? '#08D9D6' : '#1DCBB3', filter: 'url(#shadowResp)' }}
                                            animationDuration={1500}
                                            animationEasing="ease-out"
                                        />
                                    </LineChart>
                                </ResponsiveContainer>
                            ) : (
                                <div className="absolute inset-0 flex flex-col items-center justify-center text-darkTeal/40 dark:text-light/40 bg-slate-50/50 dark:bg-slate-900/20 rounded-xl border border-dashed border-slate-200 dark:border-slate-800 m-2">
                                    <span className="material-symbols-outlined text-4xl mb-2 opacity-50">show_chart</span>
                                    <p className="text-sm font-medium">No response time data yet</p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* ROW 2.5: Leads Over Time (Standalone) */}
            {stats && (
                <div className="mb-6 md:mb-8 bg-mint dark:bg-navy p-3 md:p-5 rounded-xl border border-teal/20 dark:border-light/20 shadow-sm text-left">
                    <div className="flex justify-between items-center mb-6">
                        <h3 className="text-lg font-semibold text-darkTeal dark:text-light">Leads Over Time</h3>
                        <select
                            value={timeRange}
                            onChange={(e) => setTimeRange(e.target.value)}
                            className="bg-mint dark:bg-navy border border-teal/20 dark:border-light/20 text-darkTeal/70 dark:text-light/70 text-sm rounded-lg px-3 py-2 outline-none cursor-pointer"
                        >
                            <option value="week">This Week</option>
                            <option value="month">This Month</option>
                            <option value="year">This Year</option>
                        </select>
                    </div>

                    <div className="relative min-h-[256px]">
                        {isRefreshing && (
                            <div className="absolute inset-0 bg-mint/50 dark:bg-navy/50 backdrop-blur-[1px] z-10 flex items-center justify-center rounded-lg">
                                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary dark:border-cyan"></div>
                            </div>
                        )}
                        <div className={`h-48 md:h-64 w-full flex items-end justify-between gap-2 px-2 transition-opacity duration-200 ${isRefreshing ? 'opacity-50' : 'opacity-100'}`}>
                            {leadsOverTimeData && leadsOverTimeData.length > 0 ? (
                                leadsOverTimeData.map((data, index) => {
                                    const max = Math.max(...(leadsOverTimeData.map(d => d.leads) || [10]));
                                    const height = max > 0 ? (data.leads / max) * 100 : 0;
                                    const displayHeight = height < 5 && data.leads > 0 ? 5 : height;

                                    return (
                                        <div key={index} className="w-full h-full flex flex-col justify-end items-center group relative gap-2">
                                            {/* Bar */}
                                            <div
                                                style={{ height: `${displayHeight}%` }}
                                                className={`w-full rounded-t-lg transition-all duration-500 relative ${index === 6
                                                    ? 'bg-gradient-to-t from-primary via-primary to-teal-400 dark:from-cyan dark:via-cyan dark:to-cyan-300 shadow-[0_0_15px_rgba(48,227,202,0.4)] dark:shadow-[0_0_15px_rgba(8,217,214,0.4)]'
                                                    : 'bg-gradient-to-t from-primary/10 to-primary/30 dark:from-cyan/10 dark:to-cyan/30 hover:from-primary/30 hover:to-primary/50 dark:hover:from-cyan/30 dark:hover:to-cyan/50'}`}
                                            >
                                                {/* Tooltip */}
                                                <div className="absolute -top-10 left-1/2 -translate-x-1/2 bg-white dark:bg-navy border border-teal/10 dark:border-light/10 text-darkTeal dark:text-light text-[10px] font-bold px-3 py-1.5 rounded-lg opacity-0 group-hover:opacity-100 transition-all duration-300 z-10 pointer-events-none whitespace-nowrap shadow-xl -translate-y-2 group-hover:translate-y-0">
                                                    {data.leads} leads
                                                </div>
                                            </div>
                                            {/* Label */}
                                            <span className={`text-[10px] uppercase tracking-wide ${index === 6 ? "font-bold text-teal dark:text-cyan" : "text-darkTeal/50 dark:text-light/50 group-hover:text-darkTeal dark:group-hover:text-light transition-colors"}`}>
                                                {data.day}
                                            </span>
                                        </div>
                                    );
                                })
                            ) : (
                                <div className="w-full h-full flex items-center justify-center text-darkTeal/30 dark:text-light/30 text-xs">No data available</div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* ROW 3: Platform Performance */}
            {stats && (
                <div className="mb-6 md:mb-8 grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
                    {/* Leads Distribution Pie */}
                    <div className="bg-mint dark:bg-navy rounded-xl border border-teal/20 dark:border-light/20 p-5 md:p-6 shadow-sm">
                        <div className="flex items-center gap-2 mb-6">
                            <span className="material-symbols-outlined text-teal dark:text-cyan">donut_large</span>
                            <h3 className="font-bold text-darkTeal dark:text-light tracking-wide text-sm">Leads Distribution</h3>
                        </div>

                        <div className="h-[300px] w-full relative">
                            {leadsBySourceData.length > 0 ? (
                                <ResponsiveContainer width="100%" height="100%">
                                    <PieChart>
                                        <Pie
                                            data={leadsBySourceData}
                                            cx="50%"
                                            cy="50%"
                                            innerRadius={70}
                                            outerRadius={100}
                                            paddingAngle={4}
                                            dataKey="value"
                                            cornerRadius={5}
                                            stroke="none"
                                        >
                                            {leadsBySourceData.map((entry, index) => (
                                                <Cell
                                                    key={`cell-${index}`}
                                                    fill={entry.color}
                                                    style={{ outline: 'none' }}
                                                    className="hover:opacity-80 transition-opacity duration-200 cursor-pointer"
                                                />
                                            ))}
                                        </Pie>
                                        <Tooltip
                                            cursor={{ fill: 'transparent' }}
                                            content={({ active, payload }) => {
                                                if (active && payload && payload.length) {
                                                    const data = payload[0];
                                                    const percent = stats?.leadsBySource?.find((s) => s.name === data.name)?.percent || 0;
                                                    return (
                                                        <div className="bg-white dark:bg-slate-900 border border-teal/20 dark:border-white/10 p-3 rounded-xl shadow-[0_10px_25px_-5px_rgba(0,0,0,0.1)] flex items-center gap-3 outline-none">
                                                            <div className="w-3 h-3 rounded-full shadow-sm ring-1 ring-black/10 dark:ring-white/10" style={{ backgroundColor: data.payload.color }}></div>
                                                            <div>
                                                                <p className="text-sm font-bold text-darkTeal dark:text-light mb-0.5 capitalize">{data.name}</p>
                                                                <p className="text-xs font-medium text-darkTeal/70 dark:text-light/70 m-0">{data.value} leads <span className="opacity-75">({percent}%)</span></p>
                                                            </div>
                                                        </div>
                                                    );
                                                }
                                                return null;
                                            }}
                                        />
                                        <Legend
                                            verticalAlign="bottom"
                                            height={36}
                                            formatter={(value) => <span className="text-xs text-darkTeal dark:text-light/70 ml-1 font-medium">{value}</span>}
                                        />
                                    </PieChart>
                                </ResponsiveContainer>
                            ) : (
                                <div className="absolute inset-0 flex flex-col items-center justify-center text-darkTeal/40 dark:text-light/40">
                                    <span className="material-symbols-outlined text-4xl mb-2 opacity-50">pie_chart</span>
                                    <p className="text-sm font-medium">No lead source data available</p>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Conversion Rate by Source */}
                    <div className="bg-mint dark:bg-navy rounded-xl border border-teal/20 dark:border-light/20 p-5 md:p-6 shadow-sm">
                        <div className="flex items-center justify-between mb-6">
                            <div className="flex items-center gap-2">
                                <span className="material-symbols-outlined text-pink-500">insights</span>
                                <h3 className="font-bold text-darkTeal dark:text-light tracking-wide text-sm">Platform ROI (Conversion)</h3>
                            </div>
                        </div>

                        <div className="space-y-5 max-h-[300px] overflow-y-auto pr-2 custom-scrollbar">
                            {stats.leadsBySource && stats.leadsBySource.length > 0 ? (
                                stats.leadsBySource.map((source, index) => {
                                    const { icon, colorClass } = getSourceIconParams(source.name);
                                    const convRate = source.conversionRate || 0;
                                    const barColor = isDark ? 'bg-cyan' : 'bg-primary';

                                    return (
                                        <div key={index} className="group">
                                            <div className="flex justify-between text-xs mb-1.5 font-medium">
                                                <div className="flex items-center gap-1.5 text-darkTeal dark:text-light">
                                                    <span className={`material-symbols-outlined text-sm ${colorClass}`}>{icon}</span>
                                                    {source.name}
                                                </div>
                                                <span className="text-teal dark:text-cyan font-bold">{convRate}%</span>
                                            </div>
                                            <div className="h-2.5 w-full bg-slate-200 dark:bg-slate-800 rounded-full overflow-hidden">
                                                <div
                                                    className={`h-full ${barColor} rounded-full transition-all duration-1000 ease-out`}
                                                    style={{ width: `${convRate}%` }}
                                                ></div>
                                            </div>
                                            <div className="flex justify-between mt-1 text-[10px] text-darkTeal/50 dark:text-light/50">
                                                <span>{source.count} total leads</span>
                                                <span>{source.convertedCount || 0} converted</span>
                                            </div>
                                        </div>
                                    );
                                })
                            ) : (
                                <div className="h-full flex flex-col items-center justify-center text-darkTeal/40 dark:text-light/40 py-10">
                                    <span className="material-symbols-outlined text-4xl mb-2 opacity-50">trending_up</span>
                                    <p className="text-sm font-medium">No conversion data yet</p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Recent Leads Table */}
            <div className="bg-mint dark:bg-navy rounded-xl border border-teal/20 dark:border-light/20 shadow-sm overflow-hidden mb-6">
                <div className="p-3 md:p-5 border-b border-teal/10 dark:border-light/10 flex items-center justify-between bg-teal/5 dark:bg-light/5">
                    <h3 className="font-bold text-lg text-darkTeal dark:text-light">Recent Leads</h3>
                    <button onClick={() => navigate('/leads')} className="text-teal hover:text-primary dark:text-cyan dark:hover:text-cyan/80 text-sm font-semibold flex items-center gap-1 transition-colors uppercase tracking-wider text-xs">
                        View All <span className="material-symbols-outlined text-sm">arrow_forward</span>
                    </button>
                </div>
                <div className="hidden md:block overflow-x-auto">
                    <table className="w-full text-left text-sm min-w-[600px]">
                        <thead className="bg-mint dark:bg-navy text-darkTeal/50 dark:text-light/50 uppercase text-xs font-semibold tracking-wider border-b border-teal/10 dark:border-light/10">
                            <tr>
                                <th className="px-6 py-4">Lead Name</th>
                                <th className="px-6 py-4">Property Interest</th>
                                <th className="px-6 py-4">Source</th>
                                <th className="px-6 py-4">Status</th>
                                <th className="px-6 py-4">Response Time</th>
                                <th className="px-6 py-4 text-right">Time Received</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-teal/10 dark:divide-light/10">
                            {recentLeads && recentLeads.length > 0 ? (
                                recentLeads.map((lead) => {
                                    const sourceParams = getSourceIconParams(lead.source_platform);
                                    const statusStyle = getStatusColor(lead.status);

                                    return (
                                        <tr key={lead.id} onClick={() => navigate(`/leads/${lead.id}`)} className="hover:bg-teal/5 dark:hover:bg-light/5 transition-colors group cursor-pointer">
                                            <td className="px-6 py-4">
                                                <div className="flex items-center gap-3">
                                                    <div className="relative">
                                                        <div className="w-10 h-10 rounded-full bg-teal/10 dark:bg-light/10 flex items-center justify-center text-teal dark:text-cyan text-sm font-bold uppercase border border-teal/20 dark:border-light/20">
                                                            {lead.lead_name?.charAt(0) || '?'}
                                                        </div>
                                                        <span className={`absolute bottom-0 right-0 w-2.5 h-2.5 bg-green-500 border border-white dark:border-navy rounded-full`}></span>
                                                    </div>
                                                    <div className="text-left">
                                                        <p className="font-bold text-darkTeal dark:text-light group-hover:text-teal dark:group-hover:text-cyan transition-colors">{lead.lead_name || 'Unknown Lead'}</p>
                                                        <p className="text-xs text-darkTeal/50 dark:text-light/50">{lead.lead_email || 'No email'}</p>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 text-darkTeal/80 dark:text-light/80">
                                                {lead.property_address || <span className="italic opacity-50">No property specified</span>}
                                            </td>
                                            <td className="px-6 py-4">
                                                <div className="flex items-center gap-2">
                                                    <span className={`material-symbols-outlined text-base ${sourceParams.colorClass}`}>{sourceParams.icon}</span>
                                                    <span className="text-darkTeal/60 dark:text-light/60">{lead.source_platform || 'Unknown'}</span>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4">
                                                <span className={`px-2.5 py-1 rounded-sm text-xs font-bold uppercase tracking-wide border ${statusStyle}`}>
                                                    {lead.status || 'New'}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4">
                                                <div className="flex items-center gap-1.5 text-primary dark:text-cyan font-bold">
                                                    <span className="material-symbols-outlined text-[16px]">bolt</span>
                                                    <span>{formatDuration(lead.response_time_seconds)}</span>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 text-right text-darkTeal/40 dark:text-light/40">{timeAgo(lead.created_at)}</td>
                                        </tr>
                                    );
                                })
                            ) : (
                                <tr>
                                    <td colSpan="6" className="p-0 border-none bg-mint/50 dark:bg-navy/50">
                                        <EmptyState
                                            icon="inbox"
                                            title="No leads yet"
                                            description="Your pipeline is waiting. Send a test lead to see how the dashboard comes alive."
                                            buttonText="Configure Channels"
                                            onAction={() => navigate('/channels')}
                                        />
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Mobile Card View (md:hidden) */}
                <div className="block md:hidden">
                    {recentLeads && recentLeads.length > 0 ? (
                        <div className="divide-y divide-teal/10 dark:divide-light/10">
                            {recentLeads.map((lead) => {
                                const sourceParams = getSourceIconParams(lead.source_platform);
                                const statusStyle = getStatusColor(lead.status);
                                return (
                                    <div
                                        key={lead.id}
                                        onClick={() => navigate(`/leads/${lead.id}`)}
                                        className="p-4 hover:bg-teal/5 dark:hover:bg-light/5 transition-colors cursor-pointer"
                                    >
                                        <div className="flex items-center justify-between mb-2">
                                            <div className="flex items-center gap-3 overflow-hidden">
                                                <div className="relative shrink-0">
                                                    <div className="w-10 h-10 rounded-full bg-teal/10 dark:bg-light/10 flex items-center justify-center text-teal dark:text-cyan text-sm font-bold uppercase border border-teal/20 dark:border-light/20">
                                                        {lead.lead_name?.charAt(0) || '?'}
                                                    </div>
                                                    <span className={`absolute bottom-0 right-0 w-2.5 h-2.5 bg-green-500 border border-white dark:border-navy rounded-full`}></span>
                                                </div>
                                                <div className="min-w-0">
                                                    <p className="font-bold text-darkTeal dark:text-light text-sm truncate text-left">{lead.lead_name || 'Unknown Lead'}</p>
                                                    <p className="text-xs text-darkTeal/50 dark:text-light/50 truncate text-left">{lead.lead_email || 'No email'}</p>
                                                </div>
                                            </div>
                                            <div className="shrink-0 flex flex-col items-end gap-1">
                                                <span className={`px-2 py-0.5 rounded-sm text-[10px] font-bold uppercase tracking-wide border ${statusStyle}`}>
                                                    {lead.status || 'New'}
                                                </span>
                                                <span className="text-[10px] text-darkTeal/40 dark:text-light/40">{timeAgo(lead.created_at)}</span>
                                            </div>
                                        </div>
                                        <div className="flex items-center justify-between">
                                            <div className="flex items-center gap-2">
                                                <span className={`material-symbols-outlined text-sm ${sourceParams.colorClass}`}>{sourceParams.icon}</span>
                                                <span className="text-xs text-darkTeal/60 dark:text-light/60">{lead.source_platform || 'Unknown'}</span>
                                            </div>
                                            <div className="flex items-center gap-3">
                                                {lead.response_time_seconds != null && (
                                                    <span className="flex items-center gap-0.5 text-[10px] text-primary dark:text-cyan font-bold">
                                                        <span className="material-symbols-outlined text-[10px]">bolt</span>
                                                        {formatDuration(lead.response_time_seconds)}
                                                    </span>
                                                )}
                                                <div className="text-xs text-darkTeal/80 dark:text-light/80 truncate max-w-[100px] text-right">
                                                    {lead.property_address || <span className="italic opacity-50">No property</span>}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    ) : (
                        <div className="py-4">
                            <EmptyState
                                icon="inbox"
                                title="No leads found yet"
                                description="Connect your channels to start receiving leads automatically."
                                buttonText="Connect Channels"
                                onAction={() => navigate('/channels')}
                                simple={true}
                            />
                        </div>
                    )}
                </div>
            </div>

            {/* Footer */}
            <div className="mt-4 md:mt-6 mb-4 flex items-center justify-between text-[11px] text-light/30">
                <p>© 2026 OffTrigger.</p>
                <div className="flex gap-4">
                    <Link className="hover:text-cyan transition-colors" to="/help">Privacy</Link>
                    <Link className="hover:text-cyan transition-colors" to="/help">Terms</Link>
                </div>
            </div>
            {/* Quick Add Lead Modal */}
            <QuickAddLead
                isOpen={showQuickAdd}
                onClose={() => setShowQuickAdd(false)}
                onSuccess={() => {
                    fetchDashboardData();
                }}
            />

            {/* Import Leads Modal */}
            <ImportLeadsModal
                isOpen={showImport}
                onClose={() => setShowImport(false)}
                onSuccess={() => {
                    fetchDashboardData();
                }}
            />
        </div >
    );
};

export default Dashboard;
